import { CheckCircle, Clock, Trash2, Info, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Course } from "@/types/course";

interface CoursesTableProps {
  courses: Course[];
  isLoading: boolean;
  onToggleActive: (courseId: string, courseTitle: string, currentStatus: boolean) => void;
  onDeleteCourse: (courseId: string, courseTitle: string) => void;
  onResubmitCourse: (courseId: string, courseTitle: string) => void;
}

const CoursesTable: React.FC<CoursesTableProps> = ({
  courses,
  isLoading,
  onToggleActive,
  onDeleteCourse,
  onResubmitCourse,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!Array.isArray(courses) || courses.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Courses Yet</h3>
        <p className="text-gray-600">
          Start by creating your first course using the button above.
        </p>
      </div>
    );
  }

  const truncateDescription = (description: string, maxLength: number = 100) => {
    if (!description) return "No description";
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + "...";
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <TooltipProvider>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Course Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Visibility</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {courses.filter(course => course !== null).map((course) => (
              <TableRow key={course.id}>
                <TableCell className="font-medium">{course.title}</TableCell>
                <TableCell className="max-w-xs">
                  {course.description && course.description.length > 100 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">
                          {truncateDescription(course.description)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p className="text-sm whitespace-pre-wrap">
                          {course.description}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span>{course.description || "No description"}</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="capitalize">
                    {course.courseType === 'call' ? 'Voice/Video Call' : course.courseType}
                  </span>
                </TableCell>
                <TableCell>${course.price}</TableCell>
                <TableCell>
                  {course.status === 'pending' ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending Approval
                    </span>
                  ) : course.status === 'rejected' ? (
                    <div className="inline-flex items-center gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Rejected
                      </span>
                      {course.rejectionReason && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-red-600 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-sm">
                              <strong>Reason for rejection:</strong> {course.rejectionReason}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    course.isActive !== false 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {course.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant={course.isActive !== false ? 'outline' : 'default'}
                      size="sm"
                      className={course.isActive !== false 
                        ? 'border-gray-500 text-gray-600 hover:bg-gray-50' 
                        : 'bg-green-600 hover:bg-green-700 text-white'
                      }
                      onClick={() => onToggleActive(course.id, course.title, course.isActive !== false)}
                    >
                      {course.isActive !== false ? 'Deactivate' : 'Activate'}
                    </Button>
                    {course.status === 'rejected' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-green-500 text-green-500 hover:bg-green-50"
                        onClick={() => onResubmitCourse(course.id, course.title)}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Resubmit
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Course</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{course.title}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-500 text-white hover:bg-red-600"
                            onClick={() => onDeleteCourse(course.id, course.title)}
                          >
                            Delete Course
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TooltipProvider>
    </div>
  );
};

export default CoursesTable;
